/**
 * Curvy AU — Order Status Page (v3)
 *
 * Design: numbered-circle banner · 6-step timeline · ETA hero with carrier label
 *         and track button · dot-style item badges · vendor·colour·size subtitle
 */

(function() {
    'use strict';

    /* ── Stage definitions — 6 visible steps (no separate tracking_updates step) ─ */

    /* Timeline stages — "pick_pack" is intentionally omitted; it's functionally
       identical to received_by_curvy from the customer's POV (an internal step
       they can't act on), so we collapse them. */
    const STAGES = [{
            key: 'order_created',
            label: 'Order received',
            banner: 'Your order has been received and is being processed.',
        },
        {
            key: 'in_transit_from_supplier',
            label: 'In transit from supplier',
            banner: 'Your items are on their way to our warehouse from the supplier.',
        },
        {
            key: 'received_by_curvy',
            label: 'Received & ready to pack',
            banner: 'Your items have arrived at our warehouse and are ready to be packed.',
        },
        {
            key: 'dispatched',
            label: 'Dispatched',
            banner: 'Your order is on its way!',
        },
        {
            key: 'delivered',
            label: 'Delivered',
            banner: 'Your order has been delivered. Enjoy!',
        },
    ];

    /* Map "synonym" stage keys onto the canonical 5-step timeline:
         - tracking_updates → dispatched (carrier-side events fold into the dispatched step)
         - pick_pack        → received_by_curvy (collapsed into the "ready to pack" step) */
    function resolveStageKey(key) {
        if (key === 'tracking_updates') return 'dispatched';
        if (key === 'pick_pack') return 'received_by_curvy';
        return key;
    }

    /* ── Order-shape helpers ─────────────────────────────────────────────── */

    /* True if order has/had a backorder at any point — drives the wording on the
       "ready to pack" stage (only orders that came via supplier get the
       "Received & ready to pack" phrasing). */
    function hadBackorder(data) {
        if (data.current_stage === 'in_transit_from_supplier') return true;
        if (data.stage_dates && data.stage_dates.in_transit_from_supplier) return true;
        if (data.items && data.items.some(function(it) { return it.status === 'in_transit_from_supplier'; })) return true;
        return false;
    }

    /* Click & Collect detection — checks server-set flag first, then sniffs the
       shipping method string. Webhook should expose either `is_click_and_collect`
       or `shipping_method`. */
    function isClickAndCollect(data) {
        if (data.is_click_and_collect === true) return true;
        if (data.shipping_method && /click/i.test(data.shipping_method)) return true;
        return false;
    }

    /* Partial fulfillment — some items have already shipped, others haven't. */
    function isPartiallyFulfilled(data) {
        if (data.is_partial === true) return true;
        if (!data.fulfillment_status) return false;
        return String(data.fulfillment_status).toLowerCase() === 'partially_fulfilled';
    }

    /* Stage label with context-aware overrides */
    function getStageLabel(stage, data) {
        if (stage.key === 'received_by_curvy') {
            return hadBackorder(data) ? 'Received & ready to pack' : 'Ready to pack';
        }
        if (isClickAndCollect(data)) {
            if (stage.key === 'dispatched') return 'Ready for pickup';
            if (stage.key === 'delivered') return 'Collected';
        }
        return stage.label;
    }

    /* Stage banner sub-line with context-aware overrides */
    function getStageBanner(stage, data) {
        if (stage.key === 'received_by_curvy' && !hadBackorder(data)) {
            return 'Your order is at our warehouse, ready to be packed.';
        }
        if (isClickAndCollect(data)) {
            if (stage.key === 'in_transit_from_supplier') {
                return 'Your items are on their way to our Pymble warehouse from the supplier — we\'ll let you know as soon as they\'re ready for collection.';
            }
            if (stage.key === 'dispatched') return 'Your order is ready to be picked up from our Pymble warehouse.';
            if (stage.key === 'delivered') return 'Your order has been collected. Thanks!';
        }
        return stage.banner;
    }

    /* ── Item badge map ─────────────────────────────────────────────────── */

    const BADGE_MAP = {
        order_created: { cls: 'order_created', label: 'Processing' },
        in_transit_from_supplier: { cls: 'in_transit', label: 'Awaiting from Supplier' },
        received_by_curvy: { cls: 'received', label: 'Ready for Dispatch' },
        pick_pack: { cls: 'pick_pack', label: 'Being Packed' },
        dispatched: { cls: 'dispatched', label: 'Dispatched' },
        tracking_updates: { cls: 'dispatched', label: 'Dispatched' },
        delivered: { cls: 'delivered', label: 'Delivered' },
        /* Click & Collect-specific badges (used as overrides, not directly from status) */
        ready_for_pickup: { cls: 'dispatched', label: 'Ready for Pickup' },
        collected: { cls: 'delivered', label: 'Collected' },
    };

    /* ── Mock data ──────────────────────────────────────────────────────── */

    const MOCK_DATA = {
        order_number: 'C621771',
        order_date: '2026-04-30',
        customer_first_name: 'Alex',
        current_stage: 'in_transit_from_supplier',
        supplier_name: 'Curvy Kate',
        supplier_eta: '2026-05-06',
        expected_dispatch_date: '2026-05-06',
        estimated_delivery_start: '2026-05-11',
        estimated_delivery_end: '2026-05-11',
        estimated_carrier: 'CouriersPlease',
        dispatched_at: null,
        tracking_number: null,
        tracking_url: null,
        tracking_carrier: null,
        delivered_at: null,
        carrier_events: [],
        order_total: '$169.90',
        stage_dates: {
            order_created: '2026-04-30',
            in_transit_from_supplier: '2026-05-02',
            received_by_curvy: null,
            pick_pack: null,
            dispatched: null,
            delivered: null,
        },
        items: [{
                sku: '5052574995249',
                title: 'Superhero Multiway Padded Plunge Bra',
                vendor: 'Curvy Kate',
                variant_title: 'Latte / 12GG',
                qty: 1,
                status: 'in_transit_from_supplier',
                eta: '2026-05-06',
                image_url: null,
            },
            {
                sku: '5052574901127',
                title: 'Hey Girls Superplunge Strapless Bra',
                vendor: 'Curvy Kate',
                variant_title: 'Latte / 12GG',
                qty: 1,
                status: 'received_by_curvy',
                eta: null,
                image_url: null,
            },
        ],
    };

    /* ── Helpers ────────────────────────────────────────────────────────── */

    function escapeHtml(str) {
        if (!str) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    /* "30 Apr 2026" */
    function formatDate(iso) {
        if (!iso) return null;
        try {
            const d = new Date(iso.includes('T') ? iso : iso + 'T00:00:00');
            return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
        } catch (_) { return iso; }
    }

    /* "Monday 11 May 2026" — used in delivery hero */
    function formatHeroDate(iso) {
        if (!iso) return '';
        try {
            const d = new Date(iso.includes('T') ? iso : iso + 'T00:00:00');
            return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        } catch (_) { return formatDate(iso) || ''; }
    }

    /* "22 Apr, 9:15 am" — used in carrier event rows, always in Sydney time */
    function formatCarrierDate(isoStr) {
        if (!isoStr) return '';
        try {
            const d = new Date(isoStr);
            const tz = 'Australia/Sydney';
            return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: tz }) +
                ', ' + d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz });
        } catch (_) { return ''; }
    }

    /**
     * Formats variant_title into subtitle parts.
     * "Latte / 12GG" → colour="Latte", size="12GG"
     * "12GG"         → size="12GG"
     * Returns "Vendor · Colour · Size 12GG" (omits empty parts).
     */
    function formatItemSubtitle(item) {
        const parts = [];
        if (item.vendor) parts.push(escapeHtml(item.vendor));
        if (item.variant_title) {
            const opts = item.variant_title.split('/').map(function(s) { return s.trim(); }).filter(Boolean);
            if (opts.length >= 2) {
                parts.push(escapeHtml(opts[0])); // colour
                parts.push('Size ' + escapeHtml(opts[opts.length - 1])); // size
            } else if (opts.length === 1) {
                parts.push('Size ' + escapeHtml(opts[0]));
            }
        }
        return parts.join(' · ');
    }

    /* Tick SVG (white, for completed circles) */
    const TICK_SVG = '<svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true"><polyline points="2,7 5,10 10,3" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    /* Chevron SVG for carrier accordion */
    const CHEVRON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="cos-carrier-accordion__chevron" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';

    /* ── Render: Header ─────────────────────────────────────────────────── */

    function renderHeader(data) {
        const el = document.querySelector('.cos-header');
        if (!el) return;
        const greeting = data.customer_first_name ? 'Hi ' + escapeHtml(data.customer_first_name) + '!' : '';
        const date = formatDate(data.order_date) || '';
        el.innerHTML =
            (greeting ? '<h1 class="cos-header__greeting">' + greeting + '</h1>' : '') +
            '<p class="cos-header__order-number">Order ' + escapeHtml(data.order_number) + '</p>' +
            (date ? '<p class="cos-header__date">placed ' + date + '</p>' : '');
    }

    /* ── Render: Delivery Hero ──────────────────────────────────────────── */

    function renderDeliveryHero(data) {
        const el = document.querySelector('.cos-delivery-hero');
        if (!el) return;

        const start = data.estimated_delivery_start;
        const end = data.estimated_delivery_end;

        /* Delivered (or Collected, for Click & Collect) */
        if (data.current_stage === 'delivered') {
            const d = data.delivered_at || end;
            const deliveredLabel = isClickAndCollect(data) ? 'COLLECTED' : 'DELIVERED';
            if (d) {
                el.innerHTML =
                    '<div class="cos-delivery-hero__inner cos-delivery-hero__inner--delivered">' +
                    '<div class="cos-delivery-hero__text">' +
                    '<p class="cos-delivery-hero__label">' + deliveredLabel + '</p>' +
                    '<p class="cos-delivery-hero__date">' + formatHeroDate(d) + '</p>' +
                    '</div>' +
                    '</div>';
                return;
            }
            el.style.display = 'none';
            return;
        }

        const ccMode = isClickAndCollect(data);
        const partial = isPartiallyFulfilled(data);

        /* For C&C, the relevant date is "ready from" — when the order becomes
           collectable. Once already ready (dispatched stage), use the date it
           was marked ready. For backordered C&C, the webhook returns
           expected_ready_for_pickup (= ship-by + 2 working days). Otherwise fall
           back to dispatch-date estimates. */
        const ccReadyFrom = ccMode ?
            (data.expected_ready_for_pickup || data.dispatched_at || data.expected_dispatch_date || start || end) :
            null;

        if (!start && !end && !ccReadyFrom) { el.style.display = 'none'; return; }

        /* Date: always include day of week */
        function fmtShort(iso) {
            try {
                const d = new Date(iso.includes('T') ? iso : iso + 'T00:00:00');
                return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
            } catch (_) { return iso; }
        }

        let dateStr;
        if (ccMode) {
            /* Single "ready from" date — no end / range, because there's no pickup deadline */
            dateStr = formatHeroDate(ccReadyFrom);
        } else if (start && end && start !== end) {
            /* Range: short weekday to keep it compact — "Wed 7 May – Fri 9 May" */
            dateStr = fmtShort(start) + ' – ' + fmtShort(end);
        } else {
            dateStr = formatHeroDate(start || end);
        }

        /* Hero label:
           - Regular order               → "ESTIMATED DELIVERY"
           - C&C, already at warehouse   → "READY FOR PICKUP FROM" (date is when it became available)
           - C&C, still awaiting supplier → "EXPECTED READY FOR PICKUP" (date is an estimate, not confirmed)
        */
        const ccAwaitingSupplier = ccMode && data.current_stage === 'in_transit_from_supplier';
        const heroLabel = ccMode
            ? (ccAwaitingSupplier ? 'EXPECTED READY FOR PICKUP FROM' : 'READY FOR PICKUP FROM')
            : 'ESTIMATED DELIVERY';

        const dispatchLine = (
                data.current_stage !== 'dispatched' &&
                data.current_stage !== 'tracking_updates' &&
                data.current_stage !== 'delivered' &&
                data.expected_dispatch_date &&
                !ccMode /* C&C already shows "ready from" as the main date — sub-line is redundant */
            ) ?
            '<p class="cos-delivery-hero__sub">Expected to ship ' + formatDate(data.expected_dispatch_date) + '</p>' :
            '';

        /* Partial-fulfillment notice — makes it clear another package is on the way.
           Shown when some items have already shipped but the rest are still pending. */
        var partialNotice = '';
        if (partial) {
            var dispDate = data.dispatched_at ? formatDate(data.dispatched_at) : null;
            var ds = data.partial_dispatched_delivery_start;
            var de = data.partial_dispatched_delivery_end;
            /* Plain ETA string (no surrounding parens) so we can prefix it cleanly with "ETA:" */
            var firstPkgEta = '';
            if (ds && de && ds !== de) firstPkgEta = fmtShort(ds) + ' – ' + fmtShort(de);
            else if (ds || de)         firstPkgEta = fmtShort(ds || de);

            partialNotice =
                '<div class="cos-partial-notice" role="note">' +
                '<strong>Some items in your order have already shipped' +
                    (dispDate    ? ' on ' + dispDate              : '') +
                    (firstPkgEta ? ' (ETA: ' + firstPkgEta + ')'  : '') +
                '.</strong>' +
                '<span>The remaining items are still being prepared and will arrive in a separate package — the estimated date above is for that second delivery.</span>' +
                '</div>';
        }

        const trackBtn = (data.tracking_url && !ccMode) ?
            '<a class="cos-delivery-hero__track" href="' + escapeHtml(data.tracking_url) + '" target="_blank" rel="noopener">Track shipment →</a>' :
            '';

        /* For C&C, expose a "View pick up times" mini-link in the hero */
        const ccLink = ccMode ?
            '<a href="#" class="cos-delivery-hero__track" data-cos-action="open-pickup-modal">View pick up times →</a>' :
            '';

        el.innerHTML =
            '<div class="cos-delivery-hero__inner">' +
            '<div class="cos-delivery-hero__text">' +
            '<p class="cos-delivery-hero__label">' + heroLabel + '</p>' +
            '<p class="cos-delivery-hero__date">' + dateStr + '</p>' +
            dispatchLine +
            '</div>' +
            trackBtn + ccLink +
            '</div>' +
            partialNotice;
    }

    /* ── Render: Stage Banner (numbered circle) ─────────────────────────── */

    function renderBanner(data) {
        const el = document.querySelector('.cos-stage-banner');
        if (!el) return;

        const displayKey = resolveStageKey(data.current_stage);
        const stageIdx = STAGES.findIndex(function(s) { return s.key === displayKey; });
        const stage = STAGES[stageIdx] || STAGES[0];

        const BANNER_TICK = '<svg width="15" height="15" viewBox="0 0 12 12" fill="none" aria-hidden="true"><polyline points="2,7 5,10 10,3" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

        const stageLabel = getStageLabel(stage, data);
        const stageBanner = getStageBanner(stage, data);

        /* Bottom line of banner — either a Click & Collect pickup notice (with modal link),
           or the latest carrier tracking event, depending on order type and stage. */
        var bottomLine = '';

        const isCCDispatched =
            isClickAndCollect(data) &&
            (data.current_stage === 'dispatched' || data.current_stage === 'tracking_updates');

        if (isCCDispatched) {
            /* C&C: replace carrier updates with collection notice + view-times link */
            bottomLine =
                '<p class="cos-stage-banner__event">' +
                'Awaiting collection at our Pymble store · ' +
                '<a href="#" class="cos-pickup-link" data-cos-action="open-pickup-modal">View pick up times</a>' +
                '</p>';
        } else if (
            (data.current_stage === 'dispatched' || data.current_stage === 'tracking_updates' || data.current_stage === 'delivered') &&
            data.carrier_events && data.carrier_events.length
        ) {
            var ev = data.carrier_events[0];
            var loc = ev.location ? ' · ' + escapeHtml(ev.location) : '';
            var dt = formatCarrierDate(ev.date);
            bottomLine =
                '<p class="cos-stage-banner__event">' +
                escapeHtml(ev.description) + loc + (dt ? ' — ' + dt : '') +
                '</p>';
        }

        el.innerHTML =
            '<div class="cos-stage-banner__tick" aria-hidden="true">' + BANNER_TICK + '</div>' +
            '<div class="cos-stage-banner__text">' +
            '<strong>' + stageLabel + '</strong>' +
            '<span>' + stageBanner + '</span>' +
            bottomLine +
            '</div>';
    }

    /* ── Render: Timeline ───────────────────────────────────────────────── */

    function renderTimeline(data) {
        const el = document.querySelector('.cos-timeline');
        if (!el) return;

        const displayKey = resolveStageKey(data.current_stage);
        const effectiveActiveIdx = STAGES.findIndex(function(s) { return s.key === displayKey; });

        /* Build the HTML for a single timeline step.
           showLine controls whether the vertical connector below the circle is rendered. */
        function buildStep(stage, i, showLine) {
            var cls = 'cos-timeline__step--pending';
            if (i < effectiveActiveIdx) cls = 'cos-timeline__step--completed';
            if (i === effectiveActiveIdx) cls = 'cos-timeline__step--active';

            /* Completed + Active → tick; Pending → empty circle */
            const circleInner = (cls !== 'cos-timeline__step--pending') ? TICK_SVG : '';

            /* Date shown below label for completed/active stages.
               Priority: stage_dates[key] → known fallback fields → order_date (last resort).
               The user confirmed "it can be the same" so order_date is a valid fallback. */
            var stepDateStr = '';
            if (i <= effectiveActiveIdx) {
                var sd = data.stage_dates;
                if (sd && sd[stage.key]) {
                    stepDateStr = formatDate(sd[stage.key]);
                } else if (stage.key === 'order_created' && data.order_date) {
                    stepDateStr = formatDate(data.order_date);
                } else if (stage.key === 'in_transit_from_supplier' && data.supplier_eta) {
                    stepDateStr = formatDate(data.supplier_eta);
                } else if (stage.key === 'dispatched' && data.dispatched_at) {
                    stepDateStr = formatDate(data.dispatched_at);
                } else if (stage.key === 'delivered' && data.delivered_at) {
                    stepDateStr = formatDate(data.delivered_at);
                }
                /* Final fallback: show order_date for any stage still missing a date */
                if (!stepDateStr && data.order_date) {
                    stepDateStr = formatDate(data.order_date);
                }
            }

            /* Meta content inside step */
            var meta = '';
            const ccMode = isClickAndCollect(data);

            if (stage.key === 'dispatched' && i <= effectiveActiveIdx) {
                if (ccMode) {
                    /* Click & Collect: no carrier tracking — show pick up info instead */
                    meta =
                        '<a href="#" class="cos-timeline__tracking-link" data-cos-action="open-pickup-modal">View pick up times →</a>';
                } else {
                    if (data.tracking_number) {
                        const carrier = data.tracking_carrier ? escapeHtml(data.tracking_carrier) + ' ' : '';
                        meta = '<p class="cos-timeline__meta">' + carrier + '#' + escapeHtml(data.tracking_number) + '</p>';
                    }
                    /* Carrier events accordion — latest event shown in the banner */
                    if (data.carrier_events && data.carrier_events.length) {
                        const eventsHtml = data.carrier_events.map(function(e) {
                            const dt = formatCarrierDate(e.date);
                            const loc = e.location ? ' · ' + escapeHtml(e.location) : '';
                            return '<p class="cos-timeline__meta cos-timeline__meta--event">' +
                                escapeHtml(e.description) + loc + (dt ? ' — ' + dt : '') + '</p>';
                        }).join('');

                        meta +=
                            '<details class="cos-carrier-accordion">' +
                            '<summary>' +
                            '<span>Show carrier updates (' + data.carrier_events.length + ')</span>' +
                            CHEVRON_SVG +
                            '</summary>' +
                            '<div class="cos-carrier-accordion__list">' + eventsHtml + '</div>' +
                            '</details>';
                    }
                }
            }

            if (stage.key === 'delivered' && i <= effectiveActiveIdx && !ccMode) {
                if (data.tracking_url) {
                    meta = '<a class="cos-timeline__tracking-link" href="' + escapeHtml(data.tracking_url) + '" target="_blank" rel="noopener">View tracking →</a>';
                }
            }

            const label = getStageLabel(stage, data);

            return (
                '<div class="cos-timeline__step ' + cls + '" role="listitem">' +
                '<div class="cos-timeline__node">' +
                '<div class="cos-timeline__circle">' + circleInner + '</div>' +
                (showLine ? '<div class="cos-timeline__line"></div>' : '') +
                '</div>' +
                '<div class="cos-timeline__content">' +
                '<p class="cos-timeline__label">' + label + '</p>' +
                (stepDateStr ? '<p class="cos-timeline__step-date">' + stepDateStr + '</p>' : '') +
                meta +
                '</div>' +
                '</div>'
            );
        }

        /* ── Split stages into past / active / future ── */

        var pastHtml = '';
        var activeHtml = '';
        var futureHtml = '';

        for (var i = 0; i < STAGES.length; i++) {
            var isLastOverall = (i === STAGES.length - 1);
            if (i < effectiveActiveIdx) {
                /* Past completed steps: all show a connecting line (except the last past step,
                   which connects downward to nothing — a clean boundary before the active step). */
                var isLastPast = (i === effectiveActiveIdx - 1);
                pastHtml += buildStep(STAGES[i], i, !isLastPast);
            } else if (i === effectiveActiveIdx) {
                /* Active step: show line if there are pending steps below */
                var hasFuture = (effectiveActiveIdx < STAGES.length - 1);
                activeHtml = buildStep(STAGES[i], i, hasFuture);
            } else {
                /* Pending future steps */
                futureHtml += buildStep(STAGES[i], i, !isLastOverall);
            }
        }

        /* ── Assemble: history accordion first, then active, then pending ── */

        var html = '';

        /* 1. History accordion — collapsed by default, at the top */
        if (pastHtml) {
            var pastCount = effectiveActiveIdx;
            var pastLabel = pastCount === 1 ? '1 completed step' : pastCount + ' completed steps';
            html +=
                '<details class="cos-timeline-history">' +
                '<summary class="cos-timeline-history__summary">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="cos-timeline-history__chevron" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>' +
                '<span>View order history (' + pastLabel + ')</span>' +
                '</summary>' +
                '<div class="cos-timeline-history__steps">' +
                pastHtml +
                '</div>' +
                '</details>';
        }

        /* 2. Active step */
        if (activeHtml) {
            html += activeHtml;
        }

        /* 3. Pending future steps */
        html += futureHtml;

        el.innerHTML = html;
    }

    /* ── Render: Items ──────────────────────────────────────────────────── */

    function renderItems(data) {
        const heading = document.querySelector('.cos-items__heading');
        const list = document.querySelector('.cos-items__list');
        if (!list) return;

        if (!data.items || data.items.length === 0) {
            const section = document.querySelector('.cos-items');
            if (section) section.style.display = 'none';
            return;
        }

        /* Update heading with item count only */
        if (heading) {
            heading.innerHTML = 'Items in this order · ' + data.items.length;
        }

        const isDelivered = (data.current_stage === 'delivered');
        const isDispatched = (data.current_stage === 'dispatched' || data.current_stage === 'tracking_updates');
        const partial = isPartiallyFulfilled(data);
        const ccMode = isClickAndCollect(data);

        const cards = data.items.map(function(item) {
            const itemBackordered = item.status === 'in_transit_from_supplier';

            /* Status hierarchy per item:
               - Backorder always wins (per-item) — even on C&C orders, the
                 backordered line still shows "Awaiting from Supplier"
               - Click & Collect (non-backordered) → "Ready for Pickup" / "Collected"
               - Delivered orders → "Delivered"
               - Dispatched orders (non-partial) → "Dispatched"
               - Partial orders → use the per-item status from the webhook
               - Otherwise → fall back to per-item status */
            var effectiveStatus;
            if (itemBackordered) {
                effectiveStatus = 'in_transit_from_supplier';
            } else if (ccMode && (isDelivered || isDispatched)) {
                effectiveStatus = isDelivered ? 'collected' : 'ready_for_pickup';
            } else if (isDelivered) {
                effectiveStatus = 'delivered';
            } else if (isDispatched && !partial) {
                effectiveStatus = 'dispatched';
            } else {
                effectiveStatus = item.status || 'order_created';
            }
            const badge = BADGE_MAP[effectiveStatus] || { cls: 'order_created', label: 'Processing' };

            const rawTitle = item.title || '';
            const displayTitle = rawTitle.length > 45 ? escapeHtml(rawTitle.substring(0, 45)) + '…' : escapeHtml(rawTitle);

            const subtitle = formatItemSubtitle(item);
            const skuLine = item.sku ? '<p class="cos-item-card__sku">SKU ' + escapeHtml(item.sku) + '</p>' : '';

            const imgHtml = item.image_url ?
                '<img class="cos-item-card__image" src="' + escapeHtml(item.image_url) + '" alt="' + escapeHtml(item.title) + '" loading="lazy" width="64" height="64">' :
                '<div class="cos-item-card__image cos-item-card__image--placeholder" aria-hidden="true"></div>';

            return (
                '<div class="cos-item-card">' +
                imgHtml +
                '<div class="cos-item-card__info">' +
                '<p class="cos-item-card__title">' + displayTitle + '</p>' +
                (subtitle ? '<p class="cos-item-card__subtitle">' + subtitle + '</p>' : '') +
                skuLine +
                '</div>' +
                '<span class="cos-badge cos-badge--' + badge.cls + '">' + badge.label + '</span>' +
                '</div>'
            );
        });

        list.innerHTML = cards.join('');
    }

    /* ── Show / hide states ─────────────────────────────────────────────── */

    function showLoading() {
        document.querySelector('.cos-loading').style.display = 'block';
        document.querySelector('.cos-content').style.display = 'none';
        document.querySelector('.cos-error').style.display = 'none';
    }

    function showContent() {
        document.querySelector('.cos-loading').style.display = 'none';
        document.querySelector('.cos-content').style.display = 'block';
        document.querySelector('.cos-error').style.display = 'none';
    }

    function showError(message) {
        document.querySelector('.cos-loading').style.display = 'none';
        document.querySelector('.cos-content').style.display = 'none';
        const errEl = document.querySelector('.cos-error');
        if (errEl) {
            errEl.style.display = 'block';
            const msgEl = errEl.querySelector('.cos-error__message');
            if (msgEl && message) msgEl.textContent = message;
        }
    }

    /* ── Click & Collect pickup modal ───────────────────────────────────── */

    function injectPickupModal() {
        if (document.getElementById('cos-pickup-modal')) return;
        const html =
            '<div id="cos-pickup-modal" class="cos-pickup-modal" hidden role="dialog" aria-modal="true" aria-labelledby="cos-pickup-modal-title">' +
            '<div class="cos-pickup-modal__backdrop" data-cos-action="close-pickup-modal"></div>' +
            '<div class="cos-pickup-modal__content" role="document">' +
            '<button type="button" class="cos-pickup-modal__close" aria-label="Close" data-cos-action="close-pickup-modal">×</button>' +
            '<h2 id="cos-pickup-modal-title" class="cos-pickup-modal__title">Click &amp; Collect</h2>' +
            '<p class="cos-pickup-modal__intro">Pick up your order at a time that suits you from our Pymble warehouse.</p>' +

            '<div class="cos-pickup-info">' +
            '<div class="cos-pickup-info__row">' +
            '<div class="cos-pickup-info__icon" aria-hidden="true">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>' +
            '</div>' +
            '<div class="cos-pickup-info__body">' +
            '<p class="cos-pickup-info__heading">Address</p>' +
            '<p class="cos-pickup-info__text">' +
            'Suite 1.02, Level 1<br>' +
            '4–10 Bridge Street<br>' +
            'Pymble NSW 2073' +
            '</p>' +
            '</div>' +
            '</div>' +

            '<div class="cos-pickup-info__row">' +
            '<div class="cos-pickup-info__icon" aria-hidden="true">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>' +
            '</div>' +
            '<div class="cos-pickup-info__body">' +
            '<p class="cos-pickup-info__heading">Collection hours</p>' +
            '<p class="cos-pickup-info__text">' +
            '<strong>Monday – Friday</strong><br>' +
            '9:00 AM – 5:00 PM' +
            '</p>' +
            '<p class="cos-pickup-info__note">Closed weekends &amp; public holidays</p>' +
            '</div>' +
            '</div>' +
            '</div>' +

            '<p class="cos-pickup-modal__perk">' +
            '<strong>✨ Private fitting room available</strong> — try on before you go, and exchange on the spot if needed.' +
            '</p>' +
            '</div>' +
            '</div>';
        const wrap = document.createElement('div');
        wrap.innerHTML = html;
        document.body.appendChild(wrap.firstChild);
    }

    function openPickupModal() {
        injectPickupModal();
        const m = document.getElementById('cos-pickup-modal');
        if (!m) return;
        m.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
    }

    function closePickupModal() {
        const m = document.getElementById('cos-pickup-modal');
        if (m) m.setAttribute('hidden', '');
        document.body.style.overflow = '';
    }

    /* Delegate clicks so any [data-cos-action="open-pickup-modal"] anywhere on
       the page (banner, hero, timeline) opens the same modal. */
    function bindModalDelegation() {
        if (window._cosModalBound) return;
        window._cosModalBound = true;
        document.addEventListener('click', function(e) {
            const t = e.target.closest('[data-cos-action]');
            if (!t) return;
            const action = t.getAttribute('data-cos-action');
            if (action === 'open-pickup-modal') {
                e.preventDefault();
                openPickupModal();
            } else if (action === 'close-pickup-modal') {
                e.preventDefault();
                closePickupModal();
            }
        });
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape') closePickupModal();
        });
    }

    /* ── Main render ────────────────────────────────────────────────────── */

    function render(data) {
        renderHeader(data);
        renderDeliveryHero(data);
        renderBanner(data);
        renderTimeline(data);
        renderItems(data);
        if (isClickAndCollect(data)) injectPickupModal();
        showContent();
    }

    /* ── Fetch and initialise ───────────────────────────────────────────── */

    function init() {
        showLoading();

        if (window.COS_USE_MOCK) {
            setTimeout(function() { render(MOCK_DATA); }, 500);
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const orderId = window.COS_TEST_ORDER_ID || params.get('id');

        if (!orderId) {
            showError('No order ID was found in this URL. Please use the link from your order confirmation email.');
            return;
        }

        const webhookUrl = window.COS_WEBHOOK_URL;
        if (!webhookUrl) {
            showError('Order tracking is being set up. Please check back shortly, or contact us for an update.');
            return;
        }

        fetch(webhookUrl + '?id=' + encodeURIComponent(orderId), {
                method: 'GET',
                headers: { 'Accept': 'application/json' },
            })
            .then(function(response) {
                if (response.status === 404) throw new Error('order_not_found');
                if (!response.ok) throw new Error('fetch_error');
                return response.json();
            })
            .then(function(data) { render(data); })
            .catch(function(err) {
                if (err.message === 'order_not_found') {
                    showError("We couldn't find an order matching this link. Please check your email or contact us.");
                } else {
                    showError('We were unable to load your order status right now. Please try again shortly.');
                }
            });
    }

    /* ── Boot ───────────────────────────────────────────────────────────── */

    if (window._cosInitialized) return;
    window._cosInitialized = true;

    bindModalDelegation();

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();